import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useActiveListing } from '../contexts/ActiveListingContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { UserMenu } from '../components/UserMenu';
import { NotificationBell } from '../components/NotificationBell';
import { TemplateThumbnail } from '../components/TemplateThumbnail';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Search, Sparkles, Home, ChevronRight, Film, LayoutGrid, FileText, Printer,
  Mail, Gift, TrendingUp, MousePointer, Layers, Eye, Shield, Star, Loader2, Menu,
  Share2, Calendar
} from 'lucide-react';
import { MobileTabBar } from '../components/MobileTabBar';

// ──────────── Left Sidebar ────────────
const LeftSidebar = ({ isAdmin, showAdminNav }) => {
  const navigate = useNavigate();
  const SidebarItem = ({ icon: Icon, label, path, testId }) => (
    <button onClick={() => navigate(path)} className="flex items-center gap-3 px-4 py-2 mx-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-white hover:text-black hover:shadow-sm transition-all w-full text-left" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={testId}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );

  return (
    <aside className="w-64 border-r border-gray-200 bg-gray-50 flex flex-col overflow-y-auto hidden md:flex" data-testid="left-sidebar">
      <div className="px-4 pt-5 pb-2">
        <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400" style={{ fontFamily: 'Outfit, sans-serif' }}>My Workspace</h3>
      </div>

      <div className="flex-1 py-2 space-y-1">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 px-4 mb-2 mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Drafts & Assets</p>
        <SidebarItem icon={Film} label="My Reels" path="/my-reels" testId="sidebar-my-reels" />
        <SidebarItem icon={LayoutGrid} label="My Carousels" path="/my-carousels" testId="sidebar-my-carousels" />
        <SidebarItem icon={Layers} label="My Campaigns" path="/campaigns" testId="sidebar-my-campaigns" />

        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 px-4 mb-2 mt-6" style={{ fontFamily: 'Outfit, sans-serif' }}>DIY — Create</p>
        <SidebarItem icon={MousePointer} label="Posts" path="/templates?type=post" testId="sidebar-diy-posts" />
        <SidebarItem icon={Layers} label="Stories" path="/templates?type=story" testId="sidebar-diy-stories" />
        <SidebarItem icon={Film} label="Reels" path="/templates?type=reel" testId="sidebar-diy-reels" />
        <SidebarItem icon={LayoutGrid} label="Carousels" path="/templates?type=carousel" testId="sidebar-diy-carousels" />
        <SidebarItem icon={Printer} label="Print" path="/print-center" testId="sidebar-diy-print" />
        <SidebarItem icon={Mail} label="Newsletter" path="/templates?type=newsletter" testId="sidebar-diy-newsletter" />

        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 px-4 mb-2 mt-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Categories</p>
        <SidebarItem icon={Gift} label="Holidays" path="/templates" testId="sidebar-holidays" />
        <SidebarItem icon={TrendingUp} label="Market Reports" path="/templates" testId="sidebar-market-reports" />
        <SidebarItem icon={Star} label="Personal Branding" path="/templates" testId="sidebar-branding" />

        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 px-4 mb-2 mt-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Tools</p>
        <SidebarItem icon={Mail} label="Email Lists" path="/email-lists" testId="sidebar-email-lists" />

        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 px-4 mb-2 mt-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Publish</p>
        <SidebarItem icon={Share2} label="Connect Accounts" path="/social-accounts" testId="sidebar-connect-accounts" />
        <SidebarItem icon={Calendar} label="Scheduled Posts" path="/scheduled-posts" testId="sidebar-scheduled-posts" />

        {showAdminNav && (
          <>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-orange-400 px-4 mb-2 mt-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin</p>
            <SidebarItem icon={FileText} label="Design Studio" path="/design-studio" testId="sidebar-design-studio" />
            <SidebarItem icon={Film} label="Reel Templates" path="/admin/reel-templates" testId="sidebar-reel-templates" />
          </>
        )}
      </div>
    </aside>
  );
};

// ──────────── Right Analytics Sidebar ────────────
const RightSidebar = ({ analytics }) => {
  const typeCounts = analytics?.type_counts || {};
  const totalContent = analytics?.total_content || 0;

  const contentBreakdown = [
    { label: 'Posts', count: typeCounts.post || 0, color: '#3B82F6' },
    { label: 'Stories', count: typeCounts.story || 0, color: '#8B5CF6' },
    { label: 'Reels', count: typeCounts.reel || 0, color: '#EC4899' },
    { label: 'Carousels', count: typeCounts.carousel || 0, color: '#F59E0B' },
    { label: 'Flyers', count: typeCounts.flyer || 0, color: '#10B981' },
    { label: 'Postcards', count: typeCounts.postcard || 0, color: '#F97316' },
  ].filter(c => c.count > 0);

  const chartData = contentBreakdown.length > 0 ? contentBreakdown.map(c => ({ name: c.label, value: c.count })) : [
    { name: 'Posts', value: 0 }, { name: 'Reels', value: 0 }, { name: 'Carousels', value: 0 },
  ];

  const recentActivity = analytics?.recent_activity || [];

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const typeIcon = (type) => {
    const map = { post: MousePointer, story: Layers, reel: Film, carousel: LayoutGrid, flyer: FileText, postcard: Mail };
    const Icon = map[type] || FileText;
    return <Icon className="h-3.5 w-3.5 flex-shrink-0" />;
  };

  return (
    <aside className="w-72 border-l border-gray-200 bg-gray-50 flex flex-col overflow-y-auto hidden xl:flex" data-testid="right-sidebar">
      {/* Total Content */}
      <div className="p-4 border-b border-gray-200">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Total Content</p>
        <p className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{totalContent}</p>
        <div className="flex gap-3 mt-2 text-xs text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <span>{analytics?.published_campaigns || 0} published</span>
          <span>{analytics?.drafts_count || 0} drafts</span>
          <span>{analytics?.scheduled_count || 0} scheduled</span>
        </div>
      </div>

      {/* Content by Type */}
      <div className="p-4 border-b border-gray-200">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>By Type</p>
        {contentBreakdown.length > 0 ? (
          <div className="space-y-2">
            {contentBreakdown.map(item => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-gray-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.label}</span>
                </div>
                <span className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>No content created yet</p>
        )}
        <div className="h-28 w-full mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '12px' }} />
              <Bar dataKey="value" fill="#000000" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Templates */}
      {analytics?.top_templates?.length > 0 && (
        <div className="p-4 border-b border-gray-200">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Top Templates</p>
          <div className="space-y-2">
            {analytics.top_templates.map((tpl, i) => (
              <div key={tpl.template_id} className="flex items-center gap-2">
                <span className="text-xs text-gray-400 w-4 font-bold">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{tpl.name}</p>
                  <p className="text-[10px] text-gray-400 capitalize">{tpl.type}</p>
                </div>
                <span className="text-xs font-bold text-gray-600">{tpl.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="p-4">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Activity</p>
        {recentActivity.length > 0 ? (
          <div className="space-y-2.5">
            {recentActivity.slice(0, 8).map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  {typeIcon(item.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      item.status === 'published' ? 'bg-green-50 text-green-600' :
                      item.status === 'scheduled' ? 'bg-blue-50 text-blue-600' :
                      'bg-gray-100 text-gray-500'
                    }`}>{item.status}</span>
                    <span className="text-[10px] text-gray-400">{formatTime(item.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>No recent activity</p>
        )}
      </div>
    </aside>
  );
};

// ──────────── Draft Card (Main Stage) ────────────
const TYPE_COLORS = {
  Post: '#3B82F6', Story: '#8B5CF6', Reel: '#EC4899', Carousel: '#F59E0B',
  Flyer: '#10B981', Postcard: '#F97316', Brochure: '#06B6D4', Campaign: '#6366F1',
};

const DraftCard = ({ draft, onClick }) => {
  const badgeColor = TYPE_COLORS[draft.format] || '#6B7280';
  const canvasData = draft.canvas_data;
  const hasCanvas = canvasData && Array.isArray(canvasData.elements) && canvasData.elements.length > 0;
  const slidesPreview = Array.isArray(draft.slides_preview) ? draft.slides_preview.filter(Boolean) : [];
  const slidesWithContent = slidesPreview.filter(s => s && Array.isArray(s.elements) && s.elements.length > 0);
  const showDeck = draft.format === 'Carousel' && (slidesPreview.length > 1 || slidesWithContent.length > 1);

  return (
    <div
      onClick={() => onClick(draft)}
      className="group flex flex-col bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
      data-testid={`draft-card-${draft.id}`}
    >
      <div className="relative aspect-[4/5] bg-gray-50 overflow-hidden flex items-center justify-center">
        {draft.thumbnail ? (
          <img src={draft.thumbnail} alt={draft.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`draft-thumb-img-${draft.id}`} />
        ) : showDeck ? (
          <div className="w-full h-full flex items-center justify-center p-4 gap-1.5" data-testid={`draft-thumb-deck-${draft.id}`}>
            {slidesPreview.slice(0, 3).map((sc, idx) => (
              <div key={idx} className="flex-1 flex items-center justify-center min-w-0 rounded-md overflow-hidden bg-white shadow-sm" style={{ maxHeight: '100%' }}>
                <TemplateThumbnail canvasData={sc} propertyData={draft.property_data} width={90} />
              </div>
            ))}
            {draft.slides_total > 3 && (
              <div className="flex-shrink-0 ml-1 text-[10px] font-semibold text-gray-400 self-end mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                +{draft.slides_total - 3}
              </div>
            )}
          </div>
        ) : hasCanvas ? (
          <div className="w-full h-full flex items-center justify-center overflow-hidden" data-testid={`draft-thumb-canvas-${draft.id}`}>
            <TemplateThumbnail canvasData={canvasData} propertyData={draft.property_data} width={300} />
          </div>
        ) : draft.property_photo ? (
          <img src={draft.property_photo} alt={draft.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`draft-thumb-photo-${draft.id}`} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3" style={{ background: `linear-gradient(135deg, ${badgeColor}08, ${badgeColor}15)` }}>
            {draft.format === 'Reel' && <Film className="h-10 w-10" style={{ color: badgeColor + '60' }} />}
            {draft.format === 'Carousel' && <LayoutGrid className="h-10 w-10" style={{ color: badgeColor + '60' }} />}
            {draft.format === 'Post' && <MousePointer className="h-10 w-10" style={{ color: badgeColor + '60' }} />}
            {draft.format === 'Story' && <Layers className="h-10 w-10" style={{ color: badgeColor + '60' }} />}
            {draft.format === 'Flyer' && <FileText className="h-10 w-10" style={{ color: badgeColor + '60' }} />}
            {draft.format === 'Postcard' && <Mail className="h-10 w-10" style={{ color: badgeColor + '60' }} />}
            {!['Reel','Carousel','Post','Story','Flyer','Postcard','Brochure'].includes(draft.format) && <FileText className="h-10 w-10" style={{ color: badgeColor + '60' }} />}
            {draft.property_address && (
              <p className="text-xs text-gray-400 px-4 text-center truncate max-w-full" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {draft.property_address}
              </p>
            )}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
          <span className="text-white text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>Click to edit</span>
        </div>
        {/* Type badge */}
        <div className="absolute top-3 left-3">
          <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full backdrop-blur-sm" style={{ backgroundColor: badgeColor + '18', color: badgeColor, fontFamily: 'Outfit, sans-serif' }}>
            {draft.format}
          </span>
        </div>
        {/* Status badge */}
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 text-[10px] font-bold rounded-full backdrop-blur-sm ${
            draft.status === 'published' ? 'bg-green-100/80 text-green-700' :
            draft.status === 'scheduled' ? 'bg-blue-100/80 text-blue-700' :
            'bg-white/80 text-gray-500'
          }`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {draft.status}
          </span>
        </div>
      </div>
      <div className="p-4 border-t border-gray-200 bg-white">
        <p className="text-sm font-semibold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{draft.title}</p>
        {draft.subtitle && (
          <p className="text-xs text-gray-400 mt-0.5 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{draft.subtitle}</p>
        )}
      </div>
    </div>
  );
};

// ──────────── Main Dashboard Page ────────────
export const DashboardPage = () => {
  const { user, logout } = useAuth();
  const { magicCreate, activeListing } = useActiveListing();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [drafts, setDrafts] = useState([]);

  // Realtor-identity onboarding nudge — show banner if agent_name/phone are blank
  const [identityNudge, setIdentityNudge] = useState(false);

  const [adminView, setAdminView] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('realprop_admin_view') === 'true';
    return false;
  });
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const showAdminNav = isAdmin && adminView;

  const toggleView = () => {
    const next = !adminView;
    setAdminView(next);
    localStorage.setItem('realprop_admin_view', String(next));
  };

  useEffect(() => {
    fetchAnalytics();
    fetchDrafts();
    // Check Realtor Identity status — encourage new users to fill it in once.
    // Dismissal is persisted in localStorage; banner re-appears if they later
    // wipe their identity fields.
    (async () => {
      try {
        const dismissed = typeof window !== 'undefined' && localStorage.getItem('realprop_identity_nudge_dismissed') === 'true';
        if (dismissed) return;
        const res = await apiClient.get('/profile/brand-kit');
        const hasName = !!(res.data?.agent_name || '').trim();
        const hasPhone = !!(res.data?.agent_phone || '').trim();
        if (!hasName || !hasPhone) setIdentityNudge(true);
      } catch (_e) { /* silent — non-critical */ }
    })();
  }, []);

  const dismissIdentityNudge = () => {
    setIdentityNudge(false);
    try { localStorage.setItem('realprop_identity_nudge_dismissed', 'true'); } catch (_e) {}
  };

  const scrapeAndGo = useCallback(async (url) => {
    setScraping(true);
    const toastId = toast.loading('Scraping listing data...');
    try {
      await magicCreate(url);
      toast.success('Listing data imported! Redirecting...', { id: toastId });
      navigate('/magic-create');
    } catch (err) {
      toast.error(err.message || 'Failed to scrape listing', { id: toastId });
    } finally {
      setScraping(false);
      setUrlInput('');
    }
  }, [magicCreate, navigate]);

  // Listen for clipper data
  useEffect(() => {
    const checkClipperData = () => {
      const raw = sessionStorage.getItem('realprop_clip');
      if (raw) {
        try {
          const clipData = JSON.parse(raw);
          sessionStorage.removeItem('realprop_clip');
          if (clipData.url) {
            scrapeAndGo(clipData.url);
          }
        } catch {}
      }
    };
    checkClipperData();
    const interval = setInterval(checkClipperData, 2000);
    return () => clearInterval(interval);
  }, [scrapeAndGo]);

  const fetchAnalytics = async () => {
    try {
      const response = await apiClient.get('/analytics/dashboard');
      setAnalytics(response.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchDrafts = async () => {
    try {
      const [reels, carousels, campaigns] = await Promise.allSettled([
        apiClient.get('/reels/compositions'),
        apiClient.get('/carousel/compositions'),
        apiClient.get('/campaigns'),
      ]);
      const allDrafts = [];

      // Campaigns (posts, stories, flyers, etc.)
      if (campaigns.status === 'fulfilled') {
        (campaigns.value.data.campaigns || []).forEach(c => {
          const type = c.type || 'post';
          const pd = c.property_data || {};
          const addr = pd.address || '';
          const formatLabel = type === 'post' ? 'Post' : type === 'story' ? 'Story' : type === 'flyer' ? 'Flyer' : type === 'postcard' ? 'Postcard' : type === 'brochure' ? 'Brochure' : type === 'carousel' ? 'Carousel' : type === 'reel' ? 'Reel' : 'Campaign';
          const title = addr ? `${addr}` : c.name || `${formatLabel} Draft`;
          const cdata = c.content_data || {};
          const tpl = cdata.template || {};
          const canvasData = cdata.canvas_data || c.canvas_data || tpl.canvas_data || null;
          const propertyPhoto = pd.main_image_url || (Array.isArray(pd.photos) && pd.photos[0]) || null;
          allDrafts.push({
            id: c.campaign_id,
            title,
            subtitle: addr && c.name ? c.name : (c.caption?.substring(0, 50) || ''),
            format: formatLabel,
            status: c.status || 'draft',
            property_address: addr,
            created_at: c.created_at || '',
            thumbnail: c.thumbnail_url || tpl.thumbnail_url || null,
            canvas_data: canvasData,
            property_data: pd,
            property_photo: propertyPhoto,
            path: type === 'reel' ? `/reel-editor?comp=${c.content_data?.composition_id || ''}` : type === 'carousel' ? `/carousel-editor?id=${c.content_data?.composition_id || ''}` : c.template_id ? `/create-campaign/${c.template_id}?campaign=${c.campaign_id}` : '/campaigns',
          });
        });
      }

      // Reels
      if (reels.status === 'fulfilled') {
        (reels.value.data.compositions || []).forEach(r => {
          const firstClipUrl = (r.tracks || []).map(t => t.source_url).find(Boolean)
            || (r.tracks_config || []).map(t => t.source_url).find(Boolean) || null;
          allDrafts.push({
            id: r.composition_id,
            title: r.name || 'Reel Draft',
            subtitle: r.property_data?.address || '',
            format: 'Reel',
            status: 'draft',
            property_address: r.property_data?.address || '',
            created_at: r.updated_at || r.created_at || '',
            thumbnail: r.thumbnail_url || r.render_url || firstClipUrl || null,
            property_photo: r.property_data?.main_image_url || (Array.isArray(r.property_data?.photos) && r.property_data.photos[0]) || null,
            property_data: r.property_data || {},
            path: `/reel-editor?comp=${r.composition_id}`,
          });
        });
      }

      // Carousels
      if (carousels.status === 'fulfilled') {
        const slideToCanvas = (slide, aspect) => {
          if (!slide) return null;
          const h = aspect === '4:5' ? 1350 : aspect === '9:16' ? 1920 : 1080;
          const bgImg = slide.background_image || slide.image_url || null;
          const layers = (slide.layers || []).map((l, i) => {
            if (l.type === 'text') {
              return {
                id: l.id || `l${i}`, type: 'text',
                x: l.x || 0, y: l.y || 0,
                width: l.width || 600,
                text: l.content || l.text || '',
                fontSize: l.fontSize || 36,
                fontFamily: l.fontFamily || 'Arial',
                fill: l.fill || l.fontColor || '#FFFFFF',
                align: l.textAlign || 'left',
                opacity: l.opacity ?? 1,
              };
            }
            if (l.type === 'image' && l.imageUrl) {
              return {
                id: l.id || `l${i}`, type: 'image',
                x: l.x || 0, y: l.y || 0,
                width: l.width || 300, height: l.height || 300,
                src: l.imageUrl, opacity: l.opacity ?? 1,
              };
            }
            if (l.type === 'shape' || l.type === 'rect') {
              return {
                id: l.id || `l${i}`, type: 'rect',
                x: l.x || 0, y: l.y || 0,
                width: l.width || 200, height: l.height || 200,
                fill: l.fill || '#888',
                cornerRadius: l.borderRadius || 0,
                opacity: l.opacity ?? 1,
              };
            }
            return null;
          }).filter(Boolean);
          return {
            width: 1080, height: h,
            backgroundColor: slide.background_color || '#1a1a2e',
            elements: [
              ...(bgImg ? [{ id: 'bg-photo', type: 'photoPlaceholder', x: 0, y: 0, width: 1080, height: h }] : []),
              ...layers,
            ],
          };
        };

        (carousels.value.data.compositions || []).forEach(c => {
          const slidesArr = Array.isArray(c.slides) ? c.slides : [];
          const previews = slidesArr.slice(0, 3).map(s => slideToCanvas(s, c.aspect_ratio)).filter(Boolean);
          const firstSlideImage = slidesArr[0]?.background_image || slidesArr[0]?.image_url || null;
          allDrafts.push({
            id: c.composition_id,
            title: c.name || 'Carousel Draft',
            subtitle: c.property_data?.address || '',
            format: 'Carousel',
            status: 'draft',
            property_address: c.property_data?.address || '',
            created_at: c.updated_at || c.created_at || '',
            thumbnail: c.thumbnail_url || null,
            canvas_data: previews[0] || null,
            slides_preview: previews,
            slides_total: slidesArr.length,
            property_photo: c.property_data?.main_image_url || (Array.isArray(c.property_data?.photos) && c.property_data.photos[0]) || firstSlideImage,
            property_data: c.property_data || {},
            path: `/carousel-editor?id=${c.composition_id}`,
          });
        });
      }

      // Sort by most recent first
      allDrafts.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setDrafts(allDrafts.slice(0, 20));
    } catch {}
  };

  const handleMagicBarSubmit = useCallback(async () => {
    const url = urlInput.trim();
    if (!url) return;
    if (!url.match(/^https?:\/\//i)) {
      toast.error('Please paste a valid listing URL');
      return;
    }
    await scrapeAndGo(url);
  }, [urlInput, scrapeAndGo]);

  const handleDraftClick = (draft) => {
    navigate(draft.path);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleMagicBarSubmit();
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dashboard-page">
      {/* ════════════ MOBILE SHELL (md:hidden) ════════════ */}
      <div className="md:hidden min-h-screen pb-24 bg-white">
        {/* Mobile Header — small logo + hamburger */}
        <header
          className="h-14 px-5 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur-xl z-40 border-b border-gray-100"
          data-testid="mobile-dashboard-header"
        >
          <Link to="/dashboard" className="flex items-baseline gap-1.5">
            <span className="text-base font-bold tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>RealProp</span>
            <span className="text-[9px] uppercase tracking-[0.25em] text-gray-300">Studio</span>
          </Link>
          <button
            onClick={() => navigate('/menu')}
            className="h-9 w-9 -mr-1 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
            data-testid="mobile-dashboard-menu"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </header>

        {/* Magic Scraper Bar */}
        <section className="px-5 pt-7">
          <h2
            className="text-[28px] font-light leading-[1.05] tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Turn a listing into{' '}
            <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>magic.</span>
          </h2>

          <div className="relative mt-6" data-testid="mobile-magic-bar">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-[18px] h-[18px] pointer-events-none" />
            {scraping && (
              <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] animate-spin text-gray-500" />
            )}
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={(e) => {
                setTimeout(() => {
                  const pasted = e.target.value.trim();
                  if (pasted.match(/^https?:\/\//i)) scrapeAndGo(pasted);
                }, 100);
              }}
              placeholder="Paste a listing URL…"
              className="w-full h-14 pl-12 pr-12 rounded-2xl border border-gray-200 bg-white text-sm shadow-sm focus:outline-none focus:border-black/40 focus:ring-2 focus:ring-black/5 transition-all"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              disabled={scraping}
              data-testid="mobile-magic-bar-input"
              inputMode="url"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
            />
          </div>
          <p className="text-[11.5px] text-gray-400 mt-2.5 px-1 leading-snug">
            Paste a Zillow, Realtor, or MLS URL to auto-generate posts, reels and flyers — or tap below for more options.
          </p>

          <button
            onClick={() => navigate('/magic-create')}
            className="mt-5 w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-[#0B0C10] text-white text-sm font-medium active:scale-[0.98] transition-transform"
            data-testid="mobile-open-magic-scraper"
          >
            <Sparkles className="h-4 w-4" />
            Open Magic Scraper
          </button>
        </section>

        {/* Recent Designs */}
        <section className="px-5 mt-10">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Recent Designs
            </h3>
            {drafts.length > 0 && (
              <button
                onClick={() => navigate('/campaigns')}
                className="text-xs text-gray-400 hover:text-black transition-colors"
                data-testid="mobile-view-all-designs"
              >
                View all
              </button>
            )}
          </div>

          {drafts.length > 0 ? (
            <div className="grid grid-cols-2 gap-3" data-testid="mobile-drafts-grid">
              {drafts.slice(0, 6).map((draft) => (
                <DraftCard key={draft.id} draft={draft} onClick={handleDraftClick} />
              ))}
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center text-center py-12 px-6 rounded-2xl border border-dashed border-gray-200 bg-gray-50/50"
              data-testid="mobile-empty-stage"
            >
              <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-gray-300" />
              </div>
              <p className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                No drafts yet
              </p>
              <p className="text-xs text-gray-400 mt-1 max-w-[240px]">
                Paste a listing URL above, or pick a format from the Create tab below.
              </p>
            </div>
          )}
        </section>

        <MobileTabBar />
      </div>

      {/* ════════════ DESKTOP SHELL (hidden md:flex) ════════════ */}
      <div className="hidden md:flex md:flex-col md:h-screen md:overflow-hidden" data-testid="dashboard-desktop">
      {/* ──── Header (Magic Bar) ──── */}
      <header className="h-16 border-b border-gray-200 flex items-center gap-2 sm:gap-3 px-3 sm:px-6 bg-white/70 backdrop-blur-xl z-40 flex-shrink-0" data-testid="magic-bar-header">
        <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0">
          <h1 className="text-lg sm:text-xl font-bold tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="dashboard-logo">RealProp</h1>
        </Link>

        <div className="relative flex-1 min-w-0 max-w-2xl mx-1 sm:mx-4 group" data-testid="magic-bar">
          <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
          {scraping && <Loader2 className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 animate-spin text-blue-500" />}
          <input
            ref={inputRef}
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={(e) => {
              setTimeout(() => {
                const pasted = e.target.value.trim();
                if (pasted.match(/^https?:\/\//i)) {
                  scrapeAndGo(pasted);
                }
              }, 100);
            }}
            placeholder="Paste listing URL..."
            className="w-full h-10 sm:h-11 pl-9 sm:pl-12 pr-9 sm:pr-12 rounded-full border-2 border-gray-200 bg-gray-50 text-sm focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all shadow-sm hover:shadow-md"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            disabled={scraping}
            data-testid="magic-bar-input"
          />
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {isAdmin && (
            <button
              onClick={toggleView}
              className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${showAdminNav ? 'bg-orange-50 border-orange-200 text-orange-600' : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-black'}`}
              data-testid="admin-view-toggle"
            >
              {showAdminNav ? <Shield className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showAdminNav ? 'Admin' : 'User'}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={toggleView}
              className={`md:hidden flex items-center justify-center h-9 w-9 rounded-full border transition-all ${showAdminNav ? 'bg-orange-50 border-orange-200 text-orange-600' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
              data-testid="admin-view-toggle-mobile"
              aria-label={showAdminNav ? 'Switch to user view' : 'Switch to admin view'}
            >
              {showAdminNav ? <Shield className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          )}
          <NotificationBell />
          <UserMenu />
        </div>
      </header>

      {/* ──── Body: Sidebar + Main Stage + Analytics ──── */}
      <div className="flex-1 flex overflow-hidden">
        <LeftSidebar isAdmin={isAdmin} showAdminNav={showAdminNav} />

        {/* ──── Main Stage ──── */}
        <main className="flex-1 overflow-y-auto bg-white p-8" data-testid="main-stage">
          {identityNudge && (
            <div
              className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-sm"
              data-testid="realtor-identity-nudge"
            >
              <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-4 w-4 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Set up your Realtor Identity once
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Add your name, phone, headshot &amp; brokerage so every Magic Create draft auto-fills with your details.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => navigate('/profile?tab=brand')}
                className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs px-3 flex-shrink-0"
                data-testid="identity-nudge-cta"
              >
                Set up now
              </Button>
              <button
                onClick={dismissIdentityNudge}
                className="text-gray-400 hover:text-gray-700 text-lg px-1 leading-none flex-shrink-0"
                aria-label="Dismiss"
                data-testid="identity-nudge-dismiss"
              >
                ×
              </button>
            </div>
          )}
          {activeListing ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {activeListing.address || 'Active Listing'}
                </h2>
                <p className="text-sm text-gray-400 mt-1">{activeListing.price} &middot; {activeListing.beds} bed &middot; {activeListing.baths} bath &middot; {activeListing.sqft} sqft</p>
              </div>

              <div className="flex gap-3 mb-8 flex-wrap">
                <Button onClick={() => navigate('/magic-create')} className="bg-black text-white hover:bg-gray-900 h-9 text-sm" data-testid="goto-magic-create">
                  <Sparkles className="h-4 w-4 mr-2" /> Open Editor
                </Button>
                <Button variant="outline" onClick={() => navigate('/reel-editor')} className="h-9 text-sm" data-testid="goto-reel-editor">
                  <Film className="h-4 w-4 mr-2" /> Make Reel
                </Button>
                <Button variant="outline" onClick={() => navigate('/carousel-editor')} className="h-9 text-sm" data-testid="goto-carousel-editor">
                  <LayoutGrid className="h-4 w-4 mr-2" /> Make Carousel
                </Button>
              </div>
            </>
          ) : (
            <div className="mb-8">
              <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="main-stage-title">
                Your Content Stage
              </h2>
              <p className="text-sm text-gray-400 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Paste a listing URL above to generate marketing assets instantly
              </p>
              <Button variant="outline" onClick={() => navigate('/magic-create')} className="mt-3 h-9 text-sm border-gray-200 text-gray-600 hover:text-black" data-testid="goto-magic-scraper-link">
                <Sparkles className="h-4 w-4 mr-2" /> Open Magic Scraper
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          )}

          {/* Drafts Grid */}
          {drafts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="drafts-grid">
              {drafts.map((draft) => (
                <DraftCard key={draft.id} draft={draft} onClick={handleDraftClick} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="empty-stage">
              <div className="w-20 h-20 rounded-2xl bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center mb-6">
                <Sparkles className="h-8 w-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No drafts yet</h3>
              <p className="text-sm text-gray-400 max-w-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Paste a Zillow, Realtor.com, or MLS URL in the Magic Bar above to auto-generate Reels, Carousels, and Flyers.
              </p>
              <Button onClick={() => inputRef.current?.focus()} className="mt-6 bg-black text-white hover:bg-gray-900 h-10 px-6" data-testid="start-magic-btn">
                <Search className="h-4 w-4 mr-2" /> Start with a URL
              </Button>
            </div>
          )}
        </main>

        <RightSidebar analytics={analytics} />
      </div>
      </div>
    </div>
  );
};
